import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  Upload,
  Button,
  Steps,
  Table,
  Result,
  Space,
  Alert,
  Typography,
  message,
  Progress,
} from 'antd';
import { InboxOutlined, ArrowLeftOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { tradesApi } from '@tradermind/shared/api';
import type { DetectPlatformResponse, ImportTradesResult } from '@tradermind/shared/types';

const { Dragger } = Upload;
const { Title, Text } = Typography;

type ImportStep = 'upload' | 'preview' | 'importing' | 'result';

export default function ImportTradesPage() {
  const { t } = useTranslation('trade');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [step, setStep] = useState<ImportStep>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [platformInfo, setPlatformInfo] = useState<DetectPlatformResponse | null>(null);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState<ImportTradesResult | null>(null);

  const detectMutation = useMutation({
    mutationFn: (formData: FormData) => tradesApi.detectPlatform(formData),
    onSuccess: (response) => {
      setPlatformInfo(response.data);
      setStep('preview');
    },
    onError: () => {
      message.error(t('import_page.detect_failed'));
    },
  });

  const importMutation = useMutation({
    mutationFn: (formData: FormData) => tradesApi.importTrades(formData, setProgress),
    onSuccess: (response) => {
      setResult(response.data);
      setStep('result');

	  // Ensure list/dashboard/stats reflect newly imported trades without manual refresh
	  queryClient.invalidateQueries({ queryKey: ['trades'] });
	  queryClient.invalidateQueries({ queryKey: ['trade-stats'] });
	  queryClient.invalidateQueries({ queryKey: ['stats'] });
	  queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: () => {
      message.error(t('messages.import_failed'));
      setStep('preview');
    },
  });

  const handleFileSelect = (file: File) => {
    setFile(file);
    const formData = new FormData();
    formData.append('file', file);
    detectMutation.mutate(formData);
    return false;
  };

  const handleImport = () => {
    if (!file || !platformInfo) return;
    setStep('importing');
    setProgress(0);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('platform', platformInfo.platform);
    // 传递用户时区，用于解析本地时间格式的平台（如 Tradovate）
    formData.append('timezone', Intl.DateTimeFormat().resolvedOptions().timeZone);
    importMutation.mutate(formData);
  };

  const handleReset = () => {
    setFile(null);
    setPlatformInfo(null);
    setResult(null);
    setProgress(0);
    setStep('upload');
  };

  const stepItems = [
    { title: t('import_page.steps.upload') },
    { title: t('import_page.steps.preview') },
    { title: t('import_page.steps.importing') },
    { title: t('import_page.steps.complete') },
  ];

  const currentStep =
    step === 'upload' ? 0 : step === 'preview' ? 1 : step === 'importing' ? 2 : 3;

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/trades')}>
            {t('import_page.back')}
          </Button>
          <Title level={4} style={{ margin: 0 }}>
            {t('import_page.title')}
          </Title>
        </Space>
      </div>

      <Steps current={currentStep} items={stepItems} style={{ marginBottom: 32 }} />

      {step === 'upload' && (
        <Card>
          <Dragger
            accept=".csv,.html,.htm"
            showUploadList={false}
            beforeUpload={handleFileSelect}
            disabled={detectMutation.isPending}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">{t('import_page.upload_hint')}</p>
            <p className="ant-upload-hint">{t('import_page.upload_desc')}</p>
          </Dragger>

          {detectMutation.isPending && (
            <div style={{ textAlign: 'center', marginTop: 24 }}>
              <Text>{t('import_page.detecting')}</Text>
            </div>
          )}

          <Alert
            message={t('import_page.supported_platforms')}
            description={t('import_page.platforms_list')}
            type="info"
            showIcon
            style={{ marginTop: 24 }}
          />
        </Card>
      )}

      {step === 'preview' && platformInfo && (
        <Card>
          <Alert
            message={t('import_page.platform_detected', { name: platformInfo.display_name })}
            description={`${t('import_page.confidence')}: ${(platformInfo.confidence * 100).toFixed(0)}% | ${t('import_page.total_rows', { count: platformInfo.total_rows })}`}
            type="success"
            showIcon
            style={{ marginBottom: 24 }}
          />

          <Title level={5}>{t('import_page.column_mapping')}</Title>
          <Table
            columns={[
              { title: t('import_page.original_column'), dataIndex: 'original', key: 'original' },
              { title: t('import_page.mapped_field'), dataIndex: 'mapped_to', key: 'mapped_to' },
            ]}
            dataSource={platformInfo.detected_columns}
            rowKey={(_, index) => index?.toString() || '0'}
            pagination={false}
            size="small"
          />

          <div style={{ marginTop: 24, textAlign: 'right' }}>
            <Space>
              <Button onClick={handleReset}>{t('import_page.reselect')}</Button>
              <Button type="primary" onClick={handleImport}>
                {t('import_page.confirm_import', { count: platformInfo.total_rows })}
              </Button>
            </Space>
          </div>
        </Card>
      )}

      {step === 'importing' && (
        <Card>
          <div style={{ textAlign: 'center', padding: 48 }}>
            <Progress type="circle" percent={progress} />
            <div style={{ marginTop: 24 }}>
              <Text>{t('import_page.importing_msg')}</Text>
            </div>
          </div>
        </Card>
      )}

      {step === 'result' && result && (
        <Card>
          <Result
            status="success"
            icon={<CheckCircleOutlined />}
            title={t('import_page.import_complete')}
            subTitle={
              <div>
                <p>{t('import_page.success_count', { count: result.successful })}</p>
                {result.skipped_duplicates > 0 && <p>{t('import_page.skipped_duplicates', { count: result.skipped_duplicates })}</p>}
                {result.failed > 0 && <p>{t('import_page.failed_count', { count: result.failed })}</p>}
              </div>
            }
            extra={[
              <Button type="primary" key="view" onClick={() => navigate('/trades')}>
                {t('import_page.view_list')}
              </Button>,
              <Button key="more" onClick={handleReset}>
                {t('import_page.continue_import')}
              </Button>,
            ]}
          />

          {result.failed_details && result.failed_details.length > 0 && (
            <Alert
              message={t('import_page.import_errors')}
              description={
                <ul>
                  {result.failed_details.slice(0, 5).map((err, i) => (
                    <li key={i}>
                      {t('import_page.row_error', { row: err.row, reason: err.reason })}
                    </li>
                  ))}
                  {result.failed_details.length > 5 && <li>{t('import_page.more_errors', { count: result.failed_details.length - 5 })}</li>}
                </ul>
              }
              type="warning"
              style={{ marginTop: 24 }}
            />
          )}
        </Card>
      )}
    </div>
  );
}
